/**
 * OutreachCompose — Inline compose modal for sending one message.
 *
 * Opens from Account list, Queue, or Prospect drawer.
 * AI-generates a personalized draft based on context.
 * Sends via LinkedIn DM, Connection Request, or Email.
 */

import React, { useState, useEffect } from 'react'
import { Linkedin, Mail, UserPlus, Sparkles, Send, Loader2 } from 'lucide-react'
import { Modal } from '@/components/UI/Modal/Modal'
import { Button } from '@/components/UI/Button/Button'
import { useToast } from '@/contexts/ToastContext'
import { http } from '@/services/api'

type Channel = 'linkedin_message' | 'linkedin_connect' | 'email'

interface OutreachComposeProps {
  isOpen: boolean
  onClose: () => void
  prospect?: {
    id: string
    name: string
    title?: string
    company?: string
    email?: string
    linkedin_url?: string
  }
  /** Pre-selected channel */
  defaultChannel?: Channel
  /** Context for AI draft generation (e.g. TAG signals, ICP fit) */
  context?: string
}

const channelConfig: Record<Channel, { icon: React.ElementType; label: string; description: string }> = {
  linkedin_message: { icon: Linkedin, label: 'LinkedIn DM', description: 'Direct message (1st-degree connections)' },
  linkedin_connect: { icon: UserPlus, label: 'Connection Request', description: 'With personalized note (2nd/3rd degree)' },
  email: { icon: Mail, label: 'Email', description: 'Send via connected email' },
}

export const OutreachCompose: React.FC<OutreachComposeProps> = ({
  isOpen,
  onClose,
  prospect,
  defaultChannel = 'linkedin_message',
  context,
}) => {
  const [channel, setChannel] = useState<Channel>(defaultChannel)
  const [message, setMessage] = useState('')
  const [generating, setGenerating] = useState(false)
  const [aiUnavailable, setAiUnavailable] = useState(false)
  const [sending, setSending] = useState(false)
  const [linkedInConnected, setLinkedInConnected] = useState<boolean | null>(null)
  const { addToast } = useToast()

  // Check LinkedIn session status when modal opens
  useEffect(() => {
    if (isOpen) {
      http<{ handoff_available: boolean }>('/linkedin-session/status')
        .then(res => setLinkedInConnected(res.handoff_available))
        .catch(() => setLinkedInConnected(false))
    }
  }, [isOpen])

  // Auto-select best available channel
  useEffect(() => {
    if (isOpen && linkedInConnected !== null) {
      if (!linkedInConnected && (channel === 'linkedin_message' || channel === 'linkedin_connect')) {
        setChannel('email')
      }
    }
  }, [isOpen, linkedInConnected, channel])

  // Auto-generate draft when modal opens (or channel changes)
  const [autoGenerated, setAutoGenerated] = useState(false)
  useEffect(() => {
    if (isOpen && prospect && !autoGenerated && !message) {
      setAutoGenerated(true)
      generateDraft()
    }
    // Reset when modal closes
    if (!isOpen) {
      setAutoGenerated(false)
      setMessage('')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prospect])

  const generateDraft = async () => {
    if (!prospect) return
    setGenerating(true)
    try {
      // Dedicated outreach endpoint (not /api/query/, which is the heavy
      // Ask-AI orchestrator). Backend builds the channel-aware prompt,
      // calls LLMService with tenant routing + cost tracking, returns
      // the draft message.
      const res = await http<{ draft_message: string; channel: string }>(
        '/outreach/generate',
        {
          method: 'POST',
          body: {
            prospect_id: prospect.id,
            channel,
            context: context || undefined,
            company_name: prospect.company || undefined,
          },
        },
      )
      if (res.draft_message) {
        setMessage(res.draft_message.trim())
      }
    } catch (err) {
      // Surface the real reason in the console so we don't lose visibility
      // again the way the bare `catch {}` did before this fix.
      console.error('Outreach draft generation failed:', err)
      setAiUnavailable(true)
    } finally {
      setGenerating(false)
    }
  }

  const handleSend = async () => {
    if (!prospect || !message.trim()) return
    setSending(true)

    try {
      if (channel === 'linkedin_message' || channel === 'linkedin_connect') {
        if (!prospect.linkedin_url) {
          addToast({ type: 'error', title: 'No LinkedIn URL', message: 'This prospect needs a LinkedIn profile URL to send via LinkedIn.' })
          setSending(false)
          return
        }
        const handoffWindow = window.open('about:blank', '_blank')
        const result = await http<{ open_url: string }>('/linkedin-session/handoff', {
          method: 'POST',
          body: {
            profile_url: prospect.linkedin_url,
            draft: message.trim(),
            prospect_id: prospect.id,
            action: channel === 'linkedin_message' ? 'message' : 'connect',
          },
        })
        await navigator.clipboard?.writeText(message.trim()).catch(() => undefined)
        if (handoffWindow) {
          handoffWindow.opener = null
          handoffWindow.location.replace(result.open_url)
        } else {
          window.open(result.open_url, '_blank', 'noopener,noreferrer')
        }
      } else {
        // Email via outreach draft system. Path is /outreach/drafts (the
        // router prefix is /api/outreach/drafts; http() prepends /api).
        await http('/outreach/drafts', {
          method: 'POST',
          body: {
            prospect_id: prospect.id,
            channel: 'email',
            subject: `Reaching out from Ricald`,
            body: message.trim(),
            auto_approve: true,
          },
        })
      }

      addToast({
        type: 'success',
        title: channel.startsWith('linkedin') ? 'LinkedIn opened' : 'Message prepared',
        message: channel.startsWith('linkedin')
          ? `Draft copied. Review and complete the action for ${prospect.name}.`
          : `Prepared for ${prospect.name}`,
      })
      setMessage('')
      onClose()
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Send failed',
        message: err instanceof Error ? err.message : 'Please try again.',
      })
    } finally {
      setSending(false)
    }
  }

  if (!isOpen || !prospect) return null

  const maxChars = channel === 'linkedin_connect' ? 300 : channel === 'linkedin_message' ? 8000 : 5000

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="xl">
      <div className="space-y-5">
        {/* Recipient */}
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--accent)' }}
          >
            {prospect.name.charAt(0)}
          </div>
          <div>
            <div className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>
              {prospect.name}
            </div>
            <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {prospect.title}{prospect.company ? ` at ${prospect.company}` : ''}
            </div>
          </div>
        </div>

        {/* Channel selector */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Send via</label>
          <div className="flex gap-2">
            {(Object.entries(channelConfig) as [Channel, typeof channelConfig[Channel]][]).map(([key, cfg]) => {
              const Icon = cfg.icon
              const isActive = channel === key
              const isLinkedIn = key === 'linkedin_message' || key === 'linkedin_connect'
              const noUrl = isLinkedIn && !prospect.linkedin_url
              const noSession = isLinkedIn && linkedInConnected === false
              const disabled = noUrl || noSession
              const tooltip = noSession
                ? 'Connect LinkedIn in Settings > Integrations'
                : noUrl
                ? 'Add LinkedIn URL to this prospect'
                : cfg.description
              return (
                <button
                  key={key}
                  onClick={() => !disabled && setChannel(key)}
                  disabled={disabled}
                  className="flex-1 flex items-center gap-2 p-3 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    background: isActive ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'var(--bg-canvas)',
                    border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                    color: disabled ? 'var(--text-muted)' : isActive ? 'var(--accent)' : 'var(--text-secondary)',
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                  title={tooltip}
                >
                  <Icon className="w-4 h-4" />
                  {cfg.label}
                </button>
              )
            })}
          </div>
          {/* Guidance when channels are unavailable */}
          {linkedInConnected === false && (
            <p className="text-sm mt-2 flex items-center gap-1" style={{ color: 'var(--state-warning)' }}>
              LinkedIn not connected.{' '}
              <a href="/app/settings?tab=integrations" className="underline" style={{ color: 'var(--accent)' }}>
                Connect in Settings
              </a>
            </p>
          )}
          {linkedInConnected && !prospect.linkedin_url && (
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
              No LinkedIn URL for this prospect — email is available.
            </p>
          )}
        </div>

        {/* Message compose */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Message</label>
            {!aiUnavailable && (
              <button
                onClick={generateDraft}
                disabled={generating}
                className="flex items-center gap-1.5 text-sm transition-colors"
                style={{ color: 'var(--accent)' }}
              >
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {generating ? 'Writing draft...' : message ? 'Regenerate' : 'Generate with AI'}
              </button>
            )}
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, maxChars))}
            placeholder={generating ? 'AI is writing a personalized draft...' : aiUnavailable ? `Write a personalized message to ${prospect.name}...` : 'Write your message...'}
            rows={channel === 'linkedin_connect' ? 4 : 6}
            disabled={generating}
            className="w-full rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            style={{
              background: 'var(--bg-canvas)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border)',
              caretColor: 'var(--accent)',
            }}
          />
          {aiUnavailable && (
            <p className="text-sm mt-2 p-2.5 rounded-lg" style={{ background: 'color-mix(in srgb, var(--accent) 8%, transparent)', color: 'var(--text-muted)' }}>
              AI drafting is temporarily unavailable. You can write your message manually.
            </p>
          )}
          <div className="flex justify-between mt-1.5">
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {channelConfig[channel].description}
            </span>
            <span className="text-sm" style={{ color: message.length > maxChars * 0.9 ? 'var(--state-danger)' : 'var(--text-muted)' }}>
              {message.length}/{maxChars}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button onClick={onClose} variant="outline">Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={sending || !message.trim() || generating}
            variant="primary"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Preparing...</>
            ) : (
              <><Send className="w-4 h-4 mr-1" /> {channel.startsWith('linkedin') ? 'Open in LinkedIn' : `Send ${channelConfig[channel].label}`}</>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
